import Http from '@/utils/http'
import { RequestType } from '@/typings/enum/Common'

export interface AuthRoleRow {
  roleId: number
  roleName: string
  roleType: number
  remark: string
  permissionData?: string
  /** 机构名称，与后端 auth_role.org_name 对应 */
  orgName?: string
  /** 父节点角色ID，与后端 auth_role.parent_id 对应 */
  parentId?: number
  /** 父节点角色名称（后端联表返回） */
  parentName?: string
}

export interface AuthPermissionRow {
  id: number
  permissionName: string
  permissionCode: string
  /** 根节点常见为 -1、0、空串或 null，与 auth_permission.parent_id 一致 */
  parentId?: string | null
  orgName?: string
  parentName?: string
  sortNo: number
}

export interface RbacPageInfo {
  pageNo: number
  pageSize: number
  totalRecord: number
  totalPage?: number
}

class Rbac extends Http {
  async pageRoles(params: { roleName?: string; orgName?: string; pageNo: number; pageSize: number }) {
    return await this.request<{
      code: number
      data: { page: RbacPageInfo; roles: AuthRoleRow[] }
    }>({
      url: '/admin/rbac/roles',
      method: RequestType.Get,
      params
    })
  }

  async lookupRolesByIds(ids: number[]) {
    if (ids.length === 0) {
      return { code: 0, data: [] as AuthRoleRow[] }
    }
    return await this.request<{ code: number; data: AuthRoleRow[] }>({
      url: '/admin/rbac/roles/by-ids',
      method: RequestType.Get,
      params: { ids: ids.join(',') }
    })
  }

  async createRole(data: {
    roleName: string
    remark?: string
    permissionData?: string
    roleType?: number
    orgName?: string
    parentId?: number
  }) {
    return await this.request({ url: '/admin/rbac/roles', method: RequestType.Post, data })
  }

  async updateRole(
    roleId: number,
    data: {
      roleName?: string
      remark?: string
      permissionData?: string
      roleType?: number
      orgName?: string
      parentId?: number
    }
  ) {
    return await this.request({ url: `/admin/rbac/roles/${String(roleId)}`, method: RequestType.Put, data })
  }

  async deleteRole(roleId: number) {
    return await this.request({ url: `/admin/rbac/roles/${String(roleId)}`, method: RequestType.Delete })
  }

  /** 刷新本地缓存（含文章权限下拉用的 auth_role 父子树），与文章发布「文章权限」同源 */
  async refreshLocalCache() {
    return await this.request<{ code: number; message?: string }>({
      url: '/crossbowSystem/refreshLocalCache',
      method: RequestType.Get
    })
  }

  async pagePermissions(params: { keyword?: string; pageNo: number; pageSize: number }) {
    return await this.request<{
      code: number
      data: { page: RbacPageInfo; permissions: AuthPermissionRow[] }
    }>({
      url: '/admin/rbac/permissions',
      method: RequestType.Get,
      params
    })
  }

  async listPermissionsTree() {
    return await this.request<{ code: number; data: AuthPermissionRow[] }>({
      url: '/admin/rbac/permissions/tree',
      method: RequestType.Get
    })
  }

  async createPermission(data: {
    permissionName: string
    permissionCode: string
    parentId?: string
    orgName?: string
    parentName?: string
    sortNo?: number
    permissionType?: number
  }) {
    return await this.request({ url: '/admin/rbac/permissions', method: RequestType.Post, data })
  }

  async updatePermission(
    id: number,
    data: {
      permissionName?: string
      permissionCode?: string
      parentId?: string
      orgName?: string
      parentName?: string
      sortNo?: number
      permissionType?: number
    }
  ) {
    return await this.request({ url: `/admin/rbac/permissions/${String(id)}`, method: RequestType.Put, data })
  }

  async getRolePermissions(roleId: number) {
    return await this.request<{ code: number; data: { permissionCodes: string[] } }>({
      url: `/admin/rbac/roles/${String(roleId)}/permissions`,
      method: RequestType.Get
    })
  }

  async saveRolePermissions(roleId: number, permissionCodes: string[]) {
    return await this.request({
      url: `/admin/rbac/roles/${String(roleId)}/permissions`,
      method: RequestType.Put,
      data: { permissionCodes }
    })
  }

  async pageUsers(params: {
    userName?: string
    userEmail?: string
    userMobile?: string
    pageNo: number
    pageSize: number
  }) {
    return await this.request({
      url: '/admin/rbac/users',
      method: RequestType.Get,
      params
    })
  }

  async createUser(data: Record<string, unknown>) {
    return await this.request({
      url: '/user/create/account',
      method: RequestType.Post,
      data
    })
  }

  async updateUserRoles(userId: string, roleIds: number[]) {
    return await this.request({
      url: `/admin/rbac/users/${userId}/roles`,
      method: RequestType.Put,
      data: { roleIds }
    })
  }

  async updateUser(
    userId: string,
    data: {
      userName?: string
      userEmail?: string
      userNameCh?: string
      userMobile?: string
      originalMsgPer?: string
      msgListPage?: number
      expireTime?: string
      ifDeleted?: number
    }
  ) {
    return await this.request({ url: `/admin/rbac/users/${String(userId)}`, method: RequestType.Put, data })
  }
}

export default Rbac
